/* eslint-disable react-hooks/set-state-in-effect, react-hooks/immutability, react/no-unstable-nested-components -- pre-existing patterns in this page; refactoring is out of scope for the current A/B testing task. */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { sanitizeHtml } from '@/lib/sanitize';
import useRevalidateOnFocus from '@/hooks/useRevalidateOnFocus';
import Navbar from '@/components/Navbar';
import PaymentTrust from '@/components/PaymentTrust';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Crown, Loader2, Sparkles, Check, Info, ShoppingBag, Mail, BadgeCheck, ArrowRight,
} from 'lucide-react';

const DEFAULT_LAYOUT = [
  { id: 'hero', visible: true },
  { id: 'benefits', visible: true },
  { id: 'above', visible: false },
  { id: 'plans', visible: true },
  { id: 'notice', visible: true },
  { id: 'how-it-works', visible: true },
  { id: 'below', visible: false },
  { id: 'faq', visible: true },
  { id: 'final-cta', visible: true },
];

const BADGE_CLASSES = {
  ruby: 'bg-ruby text-white',
  gold: 'bg-gold text-black',
  none: '',
};

export default function Premium() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState(null);
  const [cms, setCms] = useState({});
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  // A/B experiment overlay on Premium copy and recommended plan.
  const [experiment, setExperiment] = useState(null); // { id, name }
  const [variant, setVariant] = useState(null);       // { key, headline, ... }

  const fetchPremiumData = useCallback(async ({ silent = false } = {}) => {
    try {
      const [plansRes, cmsRes, layoutRes] = await Promise.all([
        api.get('/plans'),
        api.get('/cms/content/premium').catch(() => ({ data: {} })),
        api.get('/cms/premium-layout').catch(() => ({ data: { sections: DEFAULT_LAYOUT } })),
      ]);
      setPlans(plansRes.data);
      setCms(cmsRes.data || {});
      const sections = layoutRes.data?.sections;
      if (Array.isArray(sections) && sections.length) setLayout(sections);
    } catch (error) {
      if (!silent) console.error('Error fetching premium data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPremiumData();
  }, [fetchPremiumData]);

  // Fetch active A/B experiment + assigned variant, then log impression once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/experiments/active${user?.id ? `?user_id=${user.id}` : ''}`);
        if (cancelled || !data?.experiment || !data?.variant) return;
        setExperiment(data.experiment);
        setVariant(data.variant);
        api.post('/experiments/event', {
          experiment_id: data.experiment.id,
          variant_key: data.variant.key,
          event: 'impression',
          user_id: user?.id || null,
        }).catch(() => {});
      } catch {
        // silent — no experiment available
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useRevalidateOnFocus(useCallback(() => fetchPremiumData({ silent: true }), [fetchPremiumData]));

  // Scroll to in-page anchor (e.g., #faq coming from Home "Saber mais")
  useEffect(() => {
    if (!loading && location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, location.hash]);

  const handleSubscribe = async (plan) => {
    // Log A/B click event before navigating away.
    if (experiment && variant) {
      api.post('/experiments/event', {
        experiment_id: experiment.id,
        variant_key: variant.key,
        event: 'click',
        user_id: user?.id || null,
      }).catch(() => {});
    }
    // If admin set a custom CTA URL, just navigate there.
    if (plan.cta_url) {
      const url = plan.cta_url;
      const isExternal = /^https?:\/\//i.test(url);
      if (isExternal) {
        window.open(url, '_blank', 'noopener');
      } else {
        navigate(url);
      }
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }
    setProcessingPlan(plan.plan_id);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/checkout/create', {
        plan_id: plan.plan_id,
        origin_url: originUrl,
      });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Erro ao processar pagamento');
      setProcessingPlan(null);
    }
  };

  // Variant overrides take precedence over CMS for the hero copy.
  const VARIANT_KEY_MAP = {
    'premium.hero.title': variant?.headline,
    'premium.hero.subtitle': variant?.subtitle,
    'premium.hero.cta': variant?.cta_text,
    'premium.hero.badge': variant?.badge_text,
  };
  const text = (key, fallback = '') => {
    const fromVariant = VARIANT_KEY_MAP[key];
    if (fromVariant) return fromVariant;
    return cms[key] ?? fallback;
  };
  const orderedSections = layout.filter((s) => s.visible !== false).map((s) => s.id);

  const planList = useMemo(() => {
    if (!plans) return [];
    const arr = Object.entries(plans)
      .map(([id, p]) => ({ ...p, plan_id: id }))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    // If the active variant requests a specific plan to be highlighted,
    // override the CMS-defined `is_recommended` flag (only one plan).
    if (variant?.highlighted_plan_id) {
      return arr.map((p) => ({
        ...p,
        is_recommended: p.plan_id === variant.highlighted_plan_id,
      }));
    }
    return arr;
  }, [plans, variant]);

  const scrollToPlans = () => {
    const el = document.getElementById('plans');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (user?.is_premium) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-4 sm:px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-brand text-3xl font-bold text-foreground mb-4">
              {text('premium.already.title', 'Já é Premium!')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {text('premium.already.subtitle', 'Aproveita todos os benefícios exclusivos da tua assinatura.')}
            </p>
            <Button
              onClick={() => navigate('/browse')}
              data-testid="explora-premium-content"
              className="bg-ruby hover:bg-ruby-light rounded-full px-8"
            >
              {text('premium.already.button', 'Explorar Conteúdo Premium')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Section renderers
  // ────────────────────────────────────────────────────────────────────────
  const sectionRenderers = {
    hero: () => (
      <section
        key="hero"
        className="relative rounded-3xl overflow-hidden border border-ruby/30 bg-gradient-to-br from-black via-[#1a0508] to-ruby/80 mb-12"
        data-testid="premium-section-hero"
      >
        <div className="absolute -top-32 -right-32 w-[26rem] h-[26rem] rounded-full bg-ruby/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
        <div className="relative z-10 px-4 sm:px-6 md:px-12 py-10 sm:py-12 md:py-20 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/20 border border-gold/40 text-gold text-xs font-medium mb-5" data-testid="premium-hero-badge">
            <Crown className="w-3 h-3" />
            {text('premium.hero.badge', 'Premium')}
          </span>
          <h1
            className="font-brand text-3xl md:text-5xl font-bold text-white max-w-3xl mx-auto mb-5"
            data-testid="premium-hero-title"
          >
            {text('premium.hero.title', 'Acede a tudo. Sem limites.')}
          </h1>
          <p
            className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8 font-reading"
            data-testid="premium-hero-subtitle"
          >
            {text('premium.hero.subtitle', 'Desbloqueia contos premium, séries exclusivas, Conhece-te, + Prazer e muito mais com um único acesso.')}
          </p>
          <Button
            onClick={scrollToPlans}
            data-testid="premium-hero-cta"
            className="bg-white text-ruby hover:bg-white/90 rounded-full px-10 py-6 font-semibold shadow-lg active:scale-95"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {text('premium.hero.cta', 'Desbloquear Premium')}
          </Button>
        </div>
      </section>
    ),

    benefits: () => {
      const items = (text('premium.benefits.items', '') || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length === 0) return null;
      return (
        <section key="benefits" className="mb-12" data-testid="premium-section-benefits">
          <div className="text-center mb-8">
            <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground">
              {text('premium.benefits.title', 'O que está incluído no Premium')}
            </h2>
            {text('premium.benefits.subtitle') && (
              <p className="text-muted-foreground text-sm md:text-base mt-2 max-w-2xl mx-auto">
                {text('premium.benefits.subtitle')}
              </p>
            )}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3" data-testid="premium-benefits-list">
            {items.map((label) => (
              <li
                key={label}
                className="flex items-start gap-3 p-4 rounded-xl bg-surface border border-border hover:border-ruby/40 transition-colors"
              >
                <span className="shrink-0 w-8 h-8 rounded-full bg-ruby/15 border border-ruby/30 flex items-center justify-center">
                  <Check className="w-4 h-4 text-ruby" />
                </span>
                <span className="text-sm md:text-base text-foreground leading-snug">{label}</span>
              </li>
            ))}
          </ul>
        </section>
      );
    },

    above: () =>
      cms['premium.above.content'] ? (
        <section
          key="above"
          className="reader-content text-foreground/80 max-w-3xl mx-auto mb-12"
          data-testid="premium-above-section"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(cms['premium.above.content']) }}
        />
      ) : null,

    plans: () => (
      <section key="plans" id="plans" className="mb-12 scroll-mt-24" data-testid="premium-section-plans">
        <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground text-center mb-2">
          Escolhe o teu plano
        </h2>
        <p className="text-muted-foreground text-sm md:text-base text-center mb-8 max-w-xl mx-auto">
          Acesso ilimitado durante todo o período escolhido. Sem renovação automática.
        </p>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-ruby" />
          </div>
        ) : planList.length > 0 ? (
          <>
            <div className="grid md:grid-cols-3 gap-6 mb-4" data-testid="premium-plans">
              {planList.map((plan) => {
                const isHighlight = !!plan.is_highlight;
                const badgeLabel = plan.badge_label || '';
                const badgeClass = BADGE_CLASSES[plan.badge_class || 'none'] || '';
                const periodLabel = plan.period_label || '';
                return (
                  <div
                    key={plan.plan_id}
                    className={`relative rounded-2xl p-8 transition-all duration-300 ${
                      isHighlight
                        ? 'bg-gradient-to-br from-ruby/20 to-gold/10 border-2 border-gold/60 shadow-lg shadow-gold/10 scale-[1.02]'
                        : 'bg-surface border border-border hover:border-ruby/50'
                    }`}
                    data-testid={`plan-${plan.plan_id}`}
                  >
                    {badgeLabel && badgeClass && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className={`px-4 py-1 text-xs font-semibold rounded-full ${badgeClass}`}>
                          {badgeLabel}
                        </span>
                      </div>
                    )}
                    <h3 className="font-brand text-2xl font-semibold text-foreground mb-2">{plan.name}</h3>
                    <p className="text-muted-foreground text-sm mb-6 min-h-[3em]">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-foreground">
                        € {Number(plan.amount || 0).toFixed(2).replace('.', ',')}
                      </span>
                      {periodLabel && <span className="text-muted-foreground ml-1">{periodLabel}</span>}
                    </div>
                    <Button
                      onClick={() => handleSubscribe(plan)}
                      disabled={processingPlan === plan.plan_id}
                      data-testid={`subscribe-${plan.plan_id}`}
                      className={`w-full rounded-full py-6 font-semibold ${
                        isHighlight
                          ? 'bg-gold hover:bg-gold-light text-black'
                          : 'bg-ruby hover:bg-ruby-light text-white'
                      }`}
                    >
                      {processingPlan === plan.plan_id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {text('premium.cta.processing', 'A processar…')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {plan.cta_label || text('premium.cta.button', 'Assinar')}
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center mb-2">
              <PaymentTrust />
            </div>
          </>
        ) : null}
      </section>
    ),

    notice: () =>
      text('premium.notice.content') ? (
        <section
          key="notice"
          className="max-w-3xl mx-auto mb-12"
          data-testid="premium-section-notice"
        >
          <div className="flex items-start gap-3 p-4 md:p-5 rounded-xl border border-gold/40 bg-gold/10">
            <span className="shrink-0 w-10 h-10 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center">
              <Mail className="w-5 h-5 text-gold" />
            </span>
            <div className="text-sm md:text-base text-foreground leading-relaxed">
              <p className="font-semibold mb-1 text-foreground">Importante</p>
              <p className="text-foreground/85">{text('premium.notice.content')}</p>
            </div>
          </div>
        </section>
      ) : null,

    'how-it-works': () => {
      const steps = [
        text('premium.howitworks.step1', 'Escolhe o teu plano.'),
        text('premium.howitworks.step2', 'Finaliza a compra no site da Pimenta Doce.'),
        text('premium.howitworks.step3', 'O acesso Premium é ativado automaticamente.'),
      ].filter(Boolean);
      if (steps.length === 0) return null;
      const icons = [ShoppingBag, Info, BadgeCheck];
      return (
        <section key="how-it-works" className="mb-12" data-testid="premium-section-how-it-works">
          <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
            {text('premium.howitworks.title', 'Como funciona')}
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {steps.map((step, i) => {
              const Icon = icons[i] || Sparkles;
              return (
                <li
                  key={i}
                  className="relative p-5 md:p-6 rounded-2xl bg-surface border border-border text-center"
                  data-testid={`howitworks-step-${i + 1}`}
                >
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-ruby text-white text-sm font-bold flex items-center justify-center shadow">
                    {i + 1}
                  </span>
                  <div className="w-12 h-12 mx-auto rounded-xl bg-ruby/15 border border-ruby/30 flex items-center justify-center mb-4 mt-2">
                    <Icon className="w-6 h-6 text-ruby" />
                  </div>
                  <p className="text-foreground text-sm md:text-base">{step}</p>
                </li>
              );
            })}
          </ol>
        </section>
      );
    },

    below: () =>
      cms['premium.below.content'] ? (
        <section
          key="below"
          className="reader-content text-foreground/80 max-w-3xl mx-auto mb-12"
          data-testid="premium-below-section"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(cms['premium.below.content']) }}
        />
      ) : null,

    faq: () =>
      cms['premium.faq.content'] ? (
        <section
          key="faq"
          id="faq"
          className="max-w-3xl mx-auto mb-12 scroll-mt-24"
          data-testid="premium-faq-section"
        >
          <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
            {text('premium.faq.title', 'Perguntas Frequentes')}
          </h2>
          <div
            className="reader-content text-foreground/80"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(cms['premium.faq.content']) }}
          />
        </section>
      ) : null,

    'final-cta': () => (
      <section
        key="final-cta"
        className="relative rounded-3xl overflow-hidden border border-gold/30 mb-12"
        data-testid="premium-section-final-cta"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-ruby via-ruby/90 to-black" />
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-ruby-light/25 blur-3xl pointer-events-none" />
        <div className="relative z-10 p-8 md:p-12 lg:p-14 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold text-black text-[11px] font-bold uppercase tracking-wider mb-4">
            <Crown className="w-3 h-3" /> Premium
          </span>
          <h2 className="font-brand text-2xl md:text-4xl font-bold text-white leading-tight mb-3 max-w-3xl mx-auto">
            {text('premium.finalcta.title', 'Pronto para explorar tudo o que a Intimate.pt tem para oferecer?')}
          </h2>
          <p className="text-white/85 text-sm md:text-base max-w-2xl mx-auto mb-7 font-reading">
            {text('premium.finalcta.subtitle', 'Escolhe o plano ideal para ti e desbloqueia toda a plataforma.')}
          </p>

          {planList.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto" data-testid="premium-final-cta-buttons">
              {planList.map((plan) => {
                const isHighlight = !!plan.is_highlight;
                return (
                  <Button
                    key={plan.plan_id}
                    onClick={() => handleSubscribe(plan)}
                    disabled={processingPlan === plan.plan_id}
                    data-testid={`final-subscribe-${plan.plan_id}`}
                    className={`w-full rounded-full py-6 font-semibold ${
                      isHighlight
                        ? 'bg-gold hover:bg-gold-light text-black'
                        : 'bg-white text-ruby hover:bg-white/90'
                    }`}
                  >
                    {processingPlan === plan.plan_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Crown className="w-4 h-4 mr-2" />
                    )}
                    {plan.name || plan.plan_id}
                    <span className="ml-1 text-xs opacity-80">
                      · € {Number(plan.amount || 0).toFixed(2).replace('.', ',')}
                    </span>
                  </Button>
                );
              })}
            </div>
          )}

          <button
            onClick={scrollToPlans}
            className="mt-5 text-white/70 hover:text-white text-xs inline-flex items-center gap-1"
            data-testid="premium-final-cta-back-to-plans"
          >
            Comparar planos em detalhe <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </section>
    ),
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-12 px-4 md:px-12">
        <div className="max-w-5xl mx-auto">
          {orderedSections.map((id) => {
            const renderer = sectionRenderers[id];
            return renderer ? renderer() : null;
          })}
        </div>
      </main>
    </div>
  );
}
